import { FitInput } from "./FitInput";
import { FitText } from "./FitText";
import { ClearIcon, ShuffleIcon } from "./Icons";
import { SideChevron } from "./Mark";
import { OrPill } from "./OrPill";
import { OptionSideKey, SIDES } from "./tokens";

/**
 * The signature device, and the only implementation of it. plan.md §2.2: two
 * solid colour fields meeting at a sharp edge, one option in each.
 *
 * It serves both screens, because they are the same object at different
 * moments:
 *
 *   `choose` — the turn screen. Shrink-to-fit text, tap a half.
 *   `write`  — the setup screen. Type into a half, with random and clear.
 *
 * One component rather than two, so the colours, the chevrons, the padding and
 * the hard edge cannot drift apart between the screen where an option is
 * written and the screen where it is chosen.
 *
 * Top/bottom rather than left/right: on a portrait phone two narrow columns
 * would force 200 characters into ~180px.
 */

export type WriteSide = {
  value: string;
  onChange: (v: string) => void;
  onRandom: () => void;
  onClear: () => void;
  locked?: boolean;
};

type Props =
  | {
      mode: "choose";
      optionA: string;
      optionB: string;
      onChoose?: (side: OptionSideKey) => void;
      chosen?: OptionSideKey;
      disabled?: boolean;
    }
  | { mode: "write"; a: WriteSide; b: WriteSide; maxLength: number };

/** Shared shell: colour and the hard edge. Setup may additionally show a marker. */
const Shell = ({
  side,
  dimmed,
  showMarker = true,
  children,
}: {
  side: OptionSideKey;
  dimmed?: boolean;
  showMarker?: boolean;
  children: React.ReactNode;
}) => (
  <div
    // `min-h-0`: a flex item defaults to `min-height: auto`, so a half whose
    // content is taller than its share refuses to shrink and pushes the seam
    // off centre — taking the OR pill, which is positioned against the
    // container's midpoint, with it. Only visible once some text was mis-fitted
    // and grew, which is why it travelled "depending on card proportions".
    className="relative min-h-0 flex-1 overflow-hidden transition-opacity duration-300"
    style={{
      backgroundColor: SIDES[side].color,
      color: SIDES[side].ink,
      // Never fully hidden: the unpicked option stays readable at reveal.
      opacity: dimmed ? 0.45 : 1,
    }}
  >
    {showMarker && (
      <span className="absolute left-5 top-4 opacity-70">
        <SideChevron marker={SIDES[side].marker} color={SIDES[side].ink} />
      </span>
    )}
    {children}
  </div>
);

const WriteHalf = ({
  side,
  field,
  maxLength,
}: {
  side: OptionSideKey;
  field: WriteSide;
  maxLength: number;
}) => {
  const ink = SIDES[side].ink;
  const remaining = maxLength - field.value.length;

  return (
    <Shell side={side}>
      {/* Identical rendering to choose mode — same element, same classes, same
          fit hook. Writing an option looks exactly like reading it. */}
      <div className="h-full w-full px-5 pb-16 pt-7">
        <FitInput
          value={field.value}
          onChange={field.onChange}
          maxLength={maxLength}
          disabled={field.locked}
          placeholder="Something bad…"
          color={ink}
        />
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-3 pb-3">
        {/* Only the countdown, and only once it is close. */}
        <span className="text-[10px] uppercase tracking-widest" style={{ color: ink, opacity: 0.55 }}>
          {remaining < 40 ? `${remaining} left` : ""}
        </span>

        <div className="pointer-events-auto flex items-center gap-2">
          <button
            onClick={field.onClear}
            disabled={field.locked || field.value.length === 0}
            aria-label="Clear this option"
            className="flex h-11 w-11 items-center justify-center rounded-full disabled:opacity-30"
            style={{ backgroundColor: `${ink}1F` }}
          >
            <ClearIcon color={ink} />
          </button>
          <button
            onClick={field.onRandom}
            disabled={field.locked}
            aria-label="Random suggestion"
            className="flex h-11 w-11 items-center justify-center rounded-full disabled:opacity-30"
            style={{ backgroundColor: ink }}
          >
            <ShuffleIcon color={SIDES[side].color} />
          </button>
        </div>
      </div>
    </Shell>
  );
};

export const Split = (props: Props) => (
  <div className="relative flex h-full w-full flex-col">
    {props.mode === "write" ? (
      <>
        <WriteHalf side={OptionSideKey.A} field={props.a} maxLength={props.maxLength} />
        <WriteHalf side={OptionSideKey.B} field={props.b} maxLength={props.maxLength} />
      </>
    ) : (
      ([OptionSideKey.A, OptionSideKey.B] as const).map((key) => {
        const text = key === OptionSideKey.A ? props.optionA : props.optionB;
        return (
          <Shell
            key={key}
            side={key}
            dimmed={props.chosen !== undefined && props.chosen !== key}
            showMarker={false}
          >
            <button
              type="button"
              disabled={props.disabled}
              onClick={() => props.onChoose?.(key)}
              aria-label={`Choose: ${text}`}
              className="h-full w-full px-5 py-6 text-left disabled:cursor-default"
            >
              <FitText max={40} min={13}>
                {text}
              </FitText>
            </button>
          </Shell>
        );
      })
    )}

    {/* On the seam, above both halves — the same hinge as in the mark. */}
    <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 justify-center">
      <OrPill />
    </div>
  </div>
);
